import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode, type TouchEvent } from 'react'
import axios from 'axios'
import {
  DndContext,
  PointerSensor,
  TouchSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core'
import { SortableContext, arrayMove, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { GripVertical, Loader2, Plus, Send, Trash2 } from 'lucide-react'
import {
  deleteAdminAccessory,
  deleteAdminAccessoryVitrineGroup,
  formatAxiosAdminError,
  getAdminAccessoryVitrineGroups,
  patchAdminAccessory,
  patchAdminAccessoryVitrineGroup,
  postAdminAccessory,
  postAdminAccessoryVitrineGroup,
  reorderAdminAccessoryVitrineGroups,
  resolveBackendMediaUrl,
  type AdminAccessoryPatch,
  type AdminAccessoryRow,
  type AdminAccessoryVitrineGroupRow,
} from '@/api'
import { adminApi } from '@/lib/api'
import { AdminGlassDisclosure } from '@/components/admin/AdminGlassDisclosure'
import { AccessoryImageUploader } from '@/components/AccessoryImageUploader'
import { ACCESSORY_VITRINE_SECTION_ORDER, compareAccessoryVitrineSections } from '@/accessorySectionOrder'
import { useHaptic } from '@/hooks/useHaptic'
import { normalizeAccessoryPhotoFile } from '@/lib/normalizeAccessoryPhoto'

const PRESET_COLORS = ['Белый', 'Черный', 'Чёрный', 'Серый'] as const

type VariantRow = AdminAccessoryVitrineGroupRow['variants'][number]

type Props = {
  catalogWrite: boolean
  token: string | null
  /** Полный список accessories — для синхронизации после сохранения */
  onChanged: () => void
}

function variantSortKey(v: VariantRow): string {
  const label = (v.variant_label || '').trim()
  const rank = label === 'Белый' ? '0' : label.includes('Черн') || label.includes('Чёрн') ? '1' : '2'
  return `${rank}:${label}:${v.id}`
}

function chipDotClass(label: string): string {
  const l = label.toLowerCase()
  if (l.includes('черн') || l.includes('чёрн') || l.includes('black')) return 'bg-gray-800'
  if (l.includes('сер')) return 'bg-gray-500'
  return 'bg-white'
}

function buildSectionOrders(groups: AdminAccessoryVitrineGroupRow[]): Record<string, AdminAccessoryVitrineGroupRow[]> {
  const m: Record<string, AdminAccessoryVitrineGroupRow[]> = {}
  for (const g of groups) {
    if ((g.layout || 'tile') !== 'tile') continue
    const sec = (g.section || '').trim() || 'Без секции'
    if (!m[sec]) m[sec] = []
    m[sec].push(g)
  }
  for (const k of Object.keys(m)) {
    m[k].sort((a, b) => a.sort_order - b.sort_order || a.id - b.id)
  }
  return m
}

function SortableVitrineCard({
  id,
  catalogWrite,
  children,
}: {
  id: number
  catalogWrite: boolean
  children: ReactNode
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id })
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.55 : 1,
  }
  return (
    <div ref={setNodeRef} style={style} className="flex gap-1.5 items-start">
      {catalogWrite && (
        <button
          type="button"
          {...attributes}
          {...listeners}
          className="touch-none cursor-grab active:cursor-grabbing shrink-0 p-1.5 mt-2.5 rounded-lg"
          style={{ color: 'rgba(255,255,255,0.35)' }}
          aria-label="Перетащить карточку"
          onPointerDown={(e) => e.stopPropagation()}
        >
          <GripVertical size={16} />
        </button>
      )}
      <div className="flex-1 min-w-0">{children}</div>
    </div>
  )
}

export function AccessoryVitrineCardAdmin({ catalogWrite, token, onChanged }: Props) {
  const haptic = useHaptic()
  const [loading, setLoading] = useState(false)
  const [groups, setGroups] = useState<AdminAccessoryVitrineGroupRow[]>([])
  const [error, setError] = useState<string | null>(null)
  const [openCardId, setOpenCardId] = useState<number | null>(null)
  const [activeVariantByGroup, setActiveVariantByGroup] = useState<Record<number, number>>({})
  const [groupDrafts, setGroupDrafts] = useState<Record<number, Partial<AdminAccessoryVitrineGroupRow>>>({})
  const [variantDrafts, setVariantDrafts] = useState<Record<number, Partial<AdminAccessoryRow>>>({})
  const [savingGroupId, setSavingGroupId] = useState<number | null>(null)
  const [savingVariantId, setSavingVariantId] = useState<number | null>(null)
  const [newSlug, setNewSlug] = useState('')
  const [newTitle, setNewTitle] = useState('')
  const [addingColorGroupId, setAddingColorGroupId] = useState<number | null>(null)
  const [newColorLabel, setNewColorLabel] = useState('')
  const [sectionOrders, setSectionOrders] = useState<Record<string, AdminAccessoryVitrineGroupRow[]>>({})
  const [dirtySections, setDirtySections] = useState<Set<string>>(() => new Set())
  const [publishingOrder, setPublishingOrder] = useState(false)
  const swipeRef = useRef<{ x: number; groupId: number } | null>(null)

  const dndSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 180, tolerance: 6 } }),
  )

  const load = useCallback(async () => {
    if (!token) return
    setLoading(true)
    setError(null)
    try {
      const data = await getAdminAccessoryVitrineGroups()
      const list = Array.isArray(data) ? data : []
      setGroups(list)
      setGroupDrafts({})
      setVariantDrafts({})
    } catch (e) {
      setGroups([])
      setError(formatAxiosAdminError(e, 'Не удалось загрузить карточки витрины.'))
    } finally {
      setLoading(false)
    }
  }, [token])

  useEffect(() => {
    void load()
  }, [load])

  useEffect(() => {
    setSectionOrders(buildSectionOrders(groups))
    setDirtySections(new Set())
  }, [groups])

  const sectionsSorted = useMemo(() => {
    const keys = Object.keys(sectionOrders)
    return keys.sort(compareAccessoryVitrineSections)
  }, [sectionOrders])

  const totalTiles = useMemo(
    () => Object.values(sectionOrders).reduce((n, arr) => n + arr.length, 0),
    [sectionOrders],
  )

  const handleTileDragEnd = useCallback(
    (section: string, event: DragEndEvent) => {
      const { active, over } = event
      if (!over || active.id === over.id) return
      setSectionOrders((prev) => {
        const list = prev[section] ?? []
        const oldIdx = list.findIndex((g) => g.id === active.id)
        const newIdx = list.findIndex((g) => g.id === over.id)
        if (oldIdx < 0 || newIdx < 0) return prev
        return { ...prev, [section]: arrayMove(list, oldIdx, newIdx) }
      })
      setDirtySections((prev) => new Set(prev).add(section))
      haptic.selection()
    },
    [haptic],
  )

  const publishTileOrder = async () => {
    if (!catalogWrite || dirtySections.size === 0) return
    setPublishingOrder(true)
    try {
      for (const sec of dirtySections) {
        const ids = (sectionOrders[sec] ?? []).map((g) => g.id)
        if (ids.length) await reorderAdminAccessoryVitrineGroups(ids)
      }
      haptic.success()
      setDirtySections(new Set())
      await load()
      onChanged()
    } catch (e) {
      if (axios.isAxiosError(e)) alert(formatAxiosAdminError(e, 'Не удалось сохранить порядок плиток.'))
    } finally {
      setPublishingOrder(false)
    }
  }

  const effectiveGroup = (g: AdminAccessoryVitrineGroupRow): AdminAccessoryVitrineGroupRow => ({
    ...g,
    ...(groupDrafts[g.id] ?? {}),
  })

  const effectiveVariant = (v: VariantRow): VariantRow & AdminAccessoryRow => {
    const d = variantDrafts[v.id] ?? {}
    return {
      ...v,
      ...d,
      id: v.id,
      name: d.name ?? v.name,
      price: d.price !== undefined ? d.price : v.price,
      image_url: d.image_url !== undefined ? d.image_url : v.image_url,
      description: d.description !== undefined ? d.description : v.description,
      sku: d.sku !== undefined ? d.sku : v.sku,
      variant_label: d.variant_label !== undefined ? d.variant_label : v.variant_label,
      is_active: d.is_active !== undefined ? d.is_active : (v.is_active ?? true),
      sort_order: d.sort_order !== undefined ? d.sort_order : (v.sort_order ?? 0),
    }
  }

  const sortedVariants = (g: AdminAccessoryVitrineGroupRow) =>
    [...g.variants].sort((a, b) => variantSortKey(a).localeCompare(variantSortKey(b), 'ru'))

  const activeVariantId = (g: AdminAccessoryVitrineGroupRow): number | null => {
    const sorted = sortedVariants(g)
    if (!sorted.length) return null
    const picked = activeVariantByGroup[g.id]
    if (picked && sorted.some((v) => v.id === picked)) return picked
    return sorted[0].id
  }

  const setActiveVariant = (groupId: number, variantId: number) => {
    setActiveVariantByGroup((prev) => ({ ...prev, [groupId]: variantId }))
    haptic.selection()
  }

  const cycleVariant = (g: AdminAccessoryVitrineGroupRow, dir: 1 | -1) => {
    const sorted = sortedVariants(g)
    if (sorted.length < 2) return
    const cur = activeVariantId(g)
    const idx = sorted.findIndex((v) => v.id === cur)
    const next = sorted[(idx + dir + sorted.length) % sorted.length]
    setActiveVariant(g.id, next.id)
  }

  const onSwipeStart = (groupId: number, e: TouchEvent) => {
    swipeRef.current = { x: e.touches[0]?.clientX ?? 0, groupId }
  }

  const onSwipeEnd = (g: AdminAccessoryVitrineGroupRow, e: TouchEvent) => {
    const start = swipeRef.current
    swipeRef.current = null
    if (!start || start.groupId !== g.id) return
    const dx = (e.changedTouches[0]?.clientX ?? 0) - start.x
    if (Math.abs(dx) < 48) return
    cycleVariant(g, dx < 0 ? 1 : -1)
  }

  const updateGroupDraft = (id: number, patch: Partial<AdminAccessoryVitrineGroupRow>) => {
    setGroupDrafts((prev) => ({ ...prev, [id]: { ...prev[id], ...patch } }))
  }

  const updateVariantDraft = (id: number, patch: Partial<AdminAccessoryRow>) => {
    setVariantDrafts((prev) => ({ ...prev, [id]: { ...prev[id], ...patch } }))
  }

  const saveGroup = async (g: AdminAccessoryVitrineGroupRow) => {
    const d = groupDrafts[g.id]
    if (!d || !catalogWrite) return
    setSavingGroupId(g.id)
    try {
      const next = await patchAdminAccessoryVitrineGroup(g.id, d)
      setGroups((prev) => prev.map((x) => (x.id === g.id ? { ...next, variants: x.variants } : x)))
      setGroupDrafts((prev) => {
        const x = { ...prev }
        delete x[g.id]
        return x
      })
      haptic.selection()
      onChanged()
    } catch (e) {
      if (axios.isAxiosError(e)) alert(formatAxiosAdminError(e, 'Не удалось сохранить карточку.'))
    } finally {
      setSavingGroupId(null)
    }
  }

  const saveVariant = async (variantId: number) => {
    const d = variantDrafts[variantId]
    if (!d || !catalogWrite) return
    setSavingVariantId(variantId)
    try {
      await patchAdminAccessory(variantId, d as AdminAccessoryPatch)
      await load()
      haptic.selection()
      onChanged()
    } catch (e) {
      if (axios.isAxiosError(e)) alert(formatAxiosAdminError(e, 'Не удалось сохранить вариант.'))
    } finally {
      setSavingVariantId(null)
    }
  }

  const onVariantImage = async (variantId: number, url: string) => {
    if (!catalogWrite) return
    try {
      await patchAdminAccessory(variantId, { image_url: url })
      await load()
      haptic.selection()
      onChanged()
    } catch {
      updateVariantDraft(variantId, { image_url: url })
    }
  }

  const createCard = async () => {
    const slug = newSlug.trim().toLowerCase()
    const title = newTitle.trim()
    if (!slug || !title || !catalogWrite) return
    try {
      const g = await postAdminAccessoryVitrineGroup({
        slug,
        title,
        layout: 'tile',
        section: 'Пульты управления',
      })
      await postAdminAccessory({
        name: `${title} · Белый`,
        price: null,
        vitrine_group_id: g.id,
        variant_label: 'Белый',
        is_active: true,
        sort_order: 0,
      })
      setNewSlug('')
      setNewTitle('')
      haptic.selection()
      await load()
      setOpenCardId(g.id)
      onChanged()
    } catch (e) {
      if (axios.isAxiosError(e)) alert(formatAxiosAdminError(e, 'Не удалось создать карточку.'))
    }
  }

  const addColorVariant = async (g: AdminAccessoryVitrineGroupRow) => {
    const label = newColorLabel.trim()
    if (!label || !catalogWrite) return
    const eg = effectiveGroup(g)
    try {
      const created = await postAdminAccessory({
        name: `${eg.title} · ${label}`,
        price: null,
        vitrine_group_id: g.id,
        variant_label: label,
        is_active: true,
        sort_order: g.variants.length * 10,
      })
      setNewColorLabel('')
      setAddingColorGroupId(null)
      haptic.selection()
      await load()
      setActiveVariant(g.id, created.id)
      onChanged()
    } catch (e) {
      if (axios.isAxiosError(e)) alert(formatAxiosAdminError(e, 'Не удалось добавить цвет.'))
    }
  }

  const uploadHero = async (groupId: number, file: File) => {
    const prepared = await normalizeAccessoryPhotoFile(file)
    const res = await adminApi.uploadAccessoryVitrineGroupImage(groupId, prepared)
    return res.data.url
  }

  const sectionHints = useMemo(() => ACCESSORY_VITRINE_SECTION_ORDER.join(' · '), [])

  function renderVitrineCard(rawG: AdminAccessoryVitrineGroupRow, gridPosition?: number) {
    const g = effectiveGroup(rawG)
    const variants = sortedVariants(rawG)
    const vid = activeVariantId(rawG)
    const activeV = variants.find((v) => v.id === vid) ?? null
    const ev = activeV ? effectiveVariant(activeV) : null
    const groupChanged = Boolean(groupDrafts[rawG.id] && Object.keys(groupDrafts[rawG.id]!).length)
    const variantChanged = activeV ? Boolean(variantDrafts[activeV.id] && Object.keys(variantDrafts[activeV.id]!).length) : false
    const hero = resolveBackendMediaUrl(g.hero_image_url ?? '')
    const variantImg = ev?.image_url ? resolveBackendMediaUrl(ev.image_url) : ''

    return (
      <AdminGlassDisclosure
        accent="violet"
        density="compact"
        open={openCardId === rawG.id}
        onOpenChange={(open) => setOpenCardId(open ? rawG.id : null)}
        title={g.title}
        subtitle={`${gridPosition != null ? `#${gridPosition} в сетке · ` : ''}${g.slug} · ${variants.length} цвет${variants.length === 1 ? '' : 'а'}`}
        badge={g.is_active ? '●' : '○'}
        headerRight={
          hero ? (
            <img src={hero} alt="" className="w-10 h-10 rounded-lg object-contain bg-black/20 shrink-0 border border-white/10" />
          ) : variantImg ? (
            <img src={variantImg} alt="" className="w-10 h-10 rounded-lg object-contain bg-black/20 shrink-0 border border-white/10" />
          ) : undefined
        }
      >
<div className="space-y-3 pt-1">
                  {/* —— Общие поля карточки —— */}
                  <div
                    className="rounded-xl p-3 space-y-2"
                    style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid var(--border-subtle)' }}
                  >
                    <p className="text-[10px] font-bold uppercase tracking-wide" style={{ color: 'var(--text-3)' }}>
                      Плитка на витрине
                    </p>
                    <label className="block text-[10px] font-bold" style={{ color: 'var(--text-3)' }}>
                      Заголовок
                      <input
                        className="input-glass w-full mt-0.5 px-2 py-2 rounded-lg text-xs"
                        disabled={!catalogWrite}
                        value={g.title}
                        onChange={(e) => updateGroupDraft(rawG.id, { title: e.target.value })}
                      />
                    </label>
                    <label className="block text-[10px] font-bold" style={{ color: 'var(--text-3)' }}>
                      Подзаголовок (коротко под названием)
                      <input
                        className="input-glass w-full mt-0.5 px-2 py-2 rounded-lg text-xs"
                        disabled={!catalogWrite}
                        value={g.subtitle ?? ''}
                        onChange={(e) => updateGroupDraft(rawG.id, { subtitle: e.target.value.trim() || null })}
                      />
                    </label>
                    <label className="block text-[10px] font-bold" style={{ color: 'var(--text-3)' }}>
                      Описание модели (общее для всех цветов)
                      <textarea
                        rows={3}
                        className="input-glass w-full mt-0.5 px-2 py-2 rounded-lg text-xs"
                        disabled={!catalogWrite}
                        value={g.description ?? ''}
                        onChange={(e) => updateGroupDraft(rawG.id, { description: e.target.value })}
                      />
                    </label>
                    <div className="grid grid-cols-2 gap-2">
                      <label className="text-[10px] font-bold" style={{ color: 'var(--text-3)' }}>
                        Секция
                        <input
                          className="input-glass w-full mt-0.5 px-2 py-1.5 rounded-lg text-xs"
                          disabled={!catalogWrite}
                          value={g.section}
                          onChange={(e) => updateGroupDraft(rawG.id, { section: e.target.value.trim() })}
                        />
                      </label>
                      <label className="text-[10px] font-bold" style={{ color: 'var(--text-3)' }}>
                        Порядок
                        <input
                          type="number"
                          className="input-glass w-full mt-0.5 px-2 py-1.5 rounded-lg text-xs"
                          disabled={!catalogWrite}
                          value={g.sort_order}
                          onChange={(e) =>
                            updateGroupDraft(rawG.id, { sort_order: Math.trunc(Number(e.target.value) || 0) })
                          }
                        />
                      </label>
                    </div>
                    {catalogWrite && (
                      <label className="block text-[10px] font-bold" style={{ color: 'var(--text-3)' }}>
                        Обложка на сетке (если пусто — фото активного цвета)
                        <input
                          type="file"
                          accept="image/*"
                          className="block mt-1 text-[10px]"
                          onChange={async (e) => {
                            const file = e.target.files?.[0]
                            if (!file) return
                            try {
                              const url = await uploadHero(rawG.id, file)
                              const next = await patchAdminAccessoryVitrineGroup(rawG.id, { hero_image_url: url })
                              setGroups((prev) =>
                                prev.map((x) => (x.id === rawG.id ? { ...next, variants: x.variants } : x)),
                              )
                              haptic.selection()
                              onChanged()
                            } catch (err) {
                              if (axios.isAxiosError(err)) alert(formatAxiosAdminError(err, 'Загрузка не удалась.'))
                            } finally {
                              e.target.value = ''
                            }
                          }}
                        />
                      </label>
                    )}
                    {catalogWrite && (
                      <button
                        type="button"
                        disabled={!groupChanged || savingGroupId === rawG.id}
                        className="press w-full py-2 rounded-xl text-[11px] font-bold disabled:opacity-40"
                        style={{
                          background: 'rgba(99,179,237,0.18)',
                          color: 'var(--cyan-bright)',
                          border: '1px solid var(--cyan-border)',
                        }}
                        onClick={() => void saveGroup(rawG)}
                      >
                        {savingGroupId === rawG.id ? 'Сохраняем карточку…' : 'Сохранить карточку'}
                      </button>
                    )}
                  </div>

                  {/* —— Цвета —— */}
                  <div>
                    <p className="text-[10px] font-bold uppercase tracking-wide mb-2 px-0.5" style={{ color: 'var(--text-3)' }}>
                      Цвета · свайп влево/вправо по редактору
                    </p>
                    <div className="flex gap-2 overflow-x-auto pb-2 snap-x snap-mandatory scrollbar-none">
                      {variants.map((v) => {
                        const label = (v.variant_label || 'Вариант').trim()
                        const active = v.id === vid
                        return (
                          <button
                            key={v.id}
                            type="button"
                            className="press snap-start shrink-0 flex items-center gap-2 px-3 py-2 rounded-xl border-2 text-xs font-semibold transition-all"
                            style={
                              active
                                ? {
                                    borderColor: 'var(--cyan)',
                                    background: 'rgba(99,179,237,0.18)',
                                    color: 'var(--cyan-bright)',
                                  }
                                : { borderColor: 'var(--border-subtle)', color: 'var(--text-2)' }
                            }
                            onClick={() => setActiveVariant(rawG.id, v.id)}
                          >
                            <span className={`w-3.5 h-3.5 rounded-full border border-white/20 ${chipDotClass(label)}`} />
                            {label}
                          </button>
                        )
                      })}
                      {catalogWrite && (
                        <button
                          type="button"
                          className="press snap-start shrink-0 flex items-center gap-1 px-3 py-2 rounded-xl text-xs font-bold"
                          style={{
                            background: 'rgba(72,187,120,0.15)',
                            color: '#68D391',
                            border: '1px dashed rgba(72,187,120,0.45)',
                          }}
                          onClick={() => {
                            setAddingColorGroupId(addingColorGroupId === rawG.id ? null : rawG.id)
                            setNewColorLabel('')
                          }}
                        >
                          <Plus size={14} /> Цвет
                        </button>
                      )}
                    </div>

                    {addingColorGroupId === rawG.id && catalogWrite && (
                      <div
                        className="rounded-xl p-2 mb-2 flex flex-wrap gap-2 items-center"
                        style={{ background: 'rgba(72,187,120,0.08)', border: '1px solid rgba(72,187,120,0.25)' }}
                      >
                        {PRESET_COLORS.map((c) => (
                          <button
                            key={c}
                            type="button"
                            className="press px-2 py-1 rounded-lg text-[10px] font-bold"
                            style={{ background: 'rgba(255,255,255,0.06)', color: 'var(--text-2)' }}
                            onClick={() => setNewColorLabel(c)}
                          >
                            {c}
                          </button>
                        ))}
                        <input
                          className="input-glass flex-1 min-w-[100px] px-2 py-1.5 rounded-lg text-xs"
                          placeholder="Название цвета"
                          value={newColorLabel}
                          onChange={(e) => setNewColorLabel(e.target.value)}
                        />
                        <button
                          type="button"
                          className="press px-3 py-1.5 rounded-lg text-[10px] font-bold"
                          style={{ background: 'rgba(72,187,120,0.3)', color: '#68D391' }}
                          onClick={() => void addColorVariant(rawG)}
                        >
                          Добавить
                        </button>
                      </div>
                    )}

                    {!activeV || !ev ? (
                      <p className="text-xs py-4 text-center" style={{ color: 'var(--text-3)' }}>
                        Нет вариантов — нажмите «+ Цвет» или создайте карточку заново.
                      </p>
                    ) : (
                      <div
                        className="rounded-xl p-3 space-y-2"
                        style={{ background: 'rgba(159,122,234,0.06)', border: '1px solid rgba(159,122,234,0.22)' }}
                        onTouchStart={(e) => onSwipeStart(rawG.id, e)}
                        onTouchEnd={(e) => onSwipeEnd(rawG, e)}
                      >
                        <p className="text-[10px] font-bold" style={{ color: '#D6BCFA' }}>
                          Редактор: {(ev.variant_label || 'вариант').trim()}
                          {variants.length > 1 && (
                            <span className="font-normal opacity-70"> · свайп для смены цвета</span>
                          )}
                        </p>
                        <div className="grid grid-cols-2 gap-2">
                          <label className="text-[10px] font-bold col-span-2" style={{ color: 'var(--text-3)' }}>
                            Название в корзине / прайсе
                            <input
                              className="input-glass w-full mt-0.5 px-2 py-2 rounded-lg text-xs"
                              disabled={!catalogWrite}
                              value={ev.name}
                              onChange={(e) => updateVariantDraft(activeV.id, { name: e.target.value })}
                            />
                          </label>
                          <label className="text-[10px] font-bold" style={{ color: 'var(--text-3)' }}>
                            Метка цвета
                            <input
                              className="input-glass w-full mt-0.5 px-2 py-2 rounded-lg text-xs"
                              disabled={!catalogWrite}
                              value={ev.variant_label ?? ''}
                              onChange={(e) =>
                                updateVariantDraft(activeV.id, { variant_label: e.target.value.trim() || null })
                              }
                            />
                          </label>
                          <label className="text-[10px] font-bold" style={{ color: 'var(--text-3)' }}>
                            SKU
                            <input
                              className="input-glass w-full mt-0.5 px-2 py-2 rounded-lg text-xs"
                              disabled={!catalogWrite}
                              value={ev.sku ?? ''}
                              onChange={(e) => updateVariantDraft(activeV.id, { sku: e.target.value.trim() || null })}
                            />
                          </label>
                          <label className="text-[10px] font-bold" style={{ color: 'var(--text-3)' }}>
                            Цена, ₽
                            <input
                              type="number"
                              className="input-glass w-full mt-0.5 px-2 py-2 rounded-lg text-xs"
                              disabled={!catalogWrite}
                              value={ev.price ?? ''}
                              onChange={(e) => {
                                const v = e.target.value
                                updateVariantDraft(activeV.id, {
                                  price: v === '' ? null : Math.max(0, Number(v) || 0),
                                })
                              }}
                            />
                          </label>
                        </div>
                        <label className="block text-[10px] font-bold" style={{ color: 'var(--text-3)' }}>
                          Описание (для этого цвета; в каталоге — если заполнено)
                          <textarea
                            rows={3}
                            className="input-glass w-full mt-0.5 px-2 py-2 rounded-lg text-xs"
                            disabled={!catalogWrite}
                            value={ev.description ?? ''}
                            onChange={(e) => updateVariantDraft(activeV.id, { description: e.target.value })}
                          />
                        </label>
                        <label className="flex items-center gap-2 text-[11px]" style={{ color: 'var(--text-2)' }}>
                          <input
                            type="checkbox"
                            disabled={!catalogWrite}
                            checked={ev.is_active ?? true}
                            onChange={(e) => updateVariantDraft(activeV.id, { is_active: e.target.checked })}
                          />
                          В продаже
                        </label>
                        {catalogWrite && (
                          <div>
                            <p className="text-[10px] font-bold mb-1" style={{ color: 'var(--text-3)' }}>
                              Фото этого цвета
                            </p>
                            <p className="text-[9px] leading-snug mb-1 opacity-80" style={{ color: 'var(--text-3)' }}>
                              При загрузке светлый фон выравнивается под белый плитки (#FFF). Для идеала — PNG без фона или перезалейте старые JPG.
                            </p>
                            <AccessoryImageUploader
                              accessoryId={activeV.id}
                              currentImageUrl={variantImg}
                              disabled={!catalogWrite}
                              onUpload={(url) => void onVariantImage(activeV.id, url)}
                            />
                          </div>
                        )}
                        {catalogWrite && (
                          <div className="flex gap-2 pt-1">
                            <button
                              type="button"
                              disabled={!variantChanged || savingVariantId === activeV.id}
                              className="press flex-1 py-2 rounded-xl text-xs font-bold disabled:opacity-40"
                              style={{
                                background: 'rgba(72,187,120,0.2)',
                                color: '#68D391',
                                border: '1px solid rgba(72,187,120,0.4)',
                              }}
                              onClick={() => void saveVariant(activeV.id)}
                            >
                              {savingVariantId === activeV.id ? 'Сохраняем…' : 'Сохранить цвет'}
                            </button>
                            {variants.length > 1 && (
                              <button
                                type="button"
                                className="press px-3 py-2 rounded-xl"
                                style={{
                                  background: 'rgba(252,129,74,0.12)',
                                  color: '#FC814A',
                                  border: '1px solid rgba(252,129,74,0.35)',
                                }}
                                title="Удалить только этот цвет"
                                onClick={async () => {
                                  if (
                                    !window.confirm(
                                      `Удалить вариант «${ev.variant_label || ev.name}»? Карточка останется.`,
                                    )
                                  )
                                    return
                                  try {
                                    await deleteAdminAccessory(activeV.id)
                                    await load()
                                    onChanged()
                                  } catch (err) {
                                    if (axios.isAxiosError(err))
                                      alert(formatAxiosAdminError(err, 'Удалить не удалось.'))
                                  }
                                }}
                              >
                                <Trash2 size={16} />
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {catalogWrite && (
                    <button
                      type="button"
                      className="press w-full py-1.5 rounded-lg text-[10px] font-bold flex items-center justify-center gap-1"
                      style={{
                        background: 'rgba(252,129,74,0.1)',
                        color: '#FC814A',
                        border: '1px solid rgba(252,129,74,0.3)',
                      }}
                      onClick={async () => {
                        if (!window.confirm(`Удалить карточку «${g.title}» и отвязать все цвета?`)) return
                        try {
                          await deleteAdminAccessoryVitrineGroup(rawG.id)
                          await load()
                          onChanged()
                        } catch (err) {
                          if (axios.isAxiosError(err)) alert(formatAxiosAdminError(err, 'Удалить не удалось.'))
                        }
                      }}
                    >
                      <Trash2 size={12} /> Удалить всю карточку
                    </button>
                  )}
                </div>

      </AdminGlassDisclosure>
    )
  }

  return (
    <AdminGlassDisclosure
      accent="cyan"
      defaultOpen
      density="compact"
      title="Карточки пультов (плитки)"
      subtitle="Одна модель → цвета внутри карточки · свайп между вариантами"
      badge={`${totalTiles}`}
    >
      <p className="text-[11px] leading-relaxed mb-3" style={{ color: 'var(--text-2)' }}>
        <strong>≡ Перетащите</strong> карточку за ручку слева — порядок плиток в каталоге (как ветки в «Товарах»).
        Внутри карточки — цвета, свайп по редактору. После перестановки нажмите «Опубликовать порядок».
      </p>

      {catalogWrite && dirtySections.size > 0 && (
        <div
          className="flex items-center justify-between px-3 py-2.5 rounded-xl mb-3 gap-2"
          style={{ background: 'rgba(246,173,85,0.12)', border: '1px solid rgba(246,173,85,0.4)' }}
        >
          <span className="text-[11px] font-semibold" style={{ color: '#F6AD55' }}>
            Порядок плиток изменён
          </span>
          <button
            type="button"
            disabled={publishingOrder}
            onClick={() => void publishTileOrder()}
            className="press flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-[11px] font-bold disabled:opacity-50 shrink-0"
            style={{ background: 'rgba(246,173,85,0.25)', color: '#F6AD55' }}
          >
            {publishingOrder ? <Loader2 size={11} className="animate-spin" /> : <Send size={11} />}
            {publishingOrder ? 'Сохраняю…' : 'Опубликовать порядок'}
          </button>
        </div>
      )}

      {error && (
        <p className="text-xs mb-2" style={{ color: '#FC814A' }}>
          {error}
        </p>
      )}

      {catalogWrite && (
        <div
          className="rounded-xl p-3 mb-3 grid grid-cols-2 gap-2"
          style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(99,179,237,0.2)' }}
        >
          <input
            className="input-glass w-full px-2 py-2 rounded-lg text-xs col-span-2"
            placeholder="slug (ps11)"
            value={newSlug}
            onChange={(e) => setNewSlug(e.target.value)}
          />
          <input
            className="input-glass w-full px-2 py-2 rounded-lg text-xs col-span-2"
            placeholder="Название (Пульт Onviz PS11)"
            value={newTitle}
            onChange={(e) => setNewTitle(e.target.value)}
          />
          <button
            type="button"
            className="press col-span-2 py-2 rounded-xl text-[11px] font-bold"
            style={{ background: 'rgba(72,187,120,0.22)', color: '#68D391', border: '1px solid rgba(72,187,120,0.4)' }}
            onClick={() => void createCard()}
          >
            + Новая карточка (с вариантом «Белый»)
          </button>
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-6">
          <Loader2 className="animate-spin" size={22} style={{ color: 'var(--cyan)' }} />
        </div>
      ) : (
        <div className="space-y-4">
          {sectionsSorted.map((section) => {
            const sectionTiles = sectionOrders[section] ?? []
            if (!sectionTiles.length) return null
            return (
              <div key={section}>
                <p
                  className="text-[10px] font-bold uppercase tracking-wide mb-2 px-1"
                  style={{ color: 'var(--text-3)' }}
                >
                  {section}
                  <span className="font-normal normal-case opacity-70"> · порядок в сетке 2×2</span>
                </p>
                <DndContext
                  sensors={dndSensors}
                  collisionDetection={closestCenter}
                  onDragEnd={(e) => handleTileDragEnd(section, e)}
                >
                  <SortableContext items={sectionTiles.map((g) => g.id)} strategy={verticalListSortingStrategy}>
                    <div className="space-y-2">
                      {sectionTiles.map((rawG, tileIdx) => (
                        <SortableVitrineCard key={rawG.id} id={rawG.id} catalogWrite={catalogWrite}>
                          {renderVitrineCard(rawG, tileIdx + 1)}
                        </SortableVitrineCard>
                      ))}
                    </div>
                  </SortableContext>
                </DndContext>
              </div>
            )
          })}
          {totalTiles === 0 && !loading && (
            <p className="text-xs text-center py-4" style={{ color: 'var(--text-3)' }}>
              Карточек пока нет. Создайте первую выше. Секции: {sectionHints}.
            </p>
          )}
        </div>
      )}
    </AdminGlassDisclosure>
  )
}
